import { useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { useAuthenticatedBlobUrl } from "@/hooks/useAuthenticatedBlobUrl";

type AuthenticatedDocumentLinkProps = {
  apiPath: string;
  label?: string;
  variant?: "thumbnail" | "link" | "card";
  className?: string;
};

export function AuthenticatedDocumentLink({
  apiPath,
  label,
  variant = "link",
  className,
}: AuthenticatedDocumentLinkProps) {
  const { blobUrl, loading, error } = useAuthenticatedBlobUrl(apiPath);
  const [imageFailed, setImageFailed] = useState(false);

  const openDocument = () => {
    if (!blobUrl) return;
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  if (variant === "card") {
    return (
      <button
        type="button"
        onClick={openDocument}
        disabled={!blobUrl || loading || error}
        className={
          className ??
          "flex w-full items-center gap-3 rounded-md border border-gray-100 px-3 py-2 text-sm font-medium text-[#0B132B] transition-colors hover:border-[#E6007E] hover:text-[#E6007E] disabled:cursor-not-allowed disabled:opacity-50"
        }
      >
        <FileText className="h-4 w-4 shrink-0 text-gray-400" />
        {loading ? "Loading…" : error ? "Unavailable" : (label ?? "Open")}
      </button>
    );
  }

  if (variant === "thumbnail") {
    return (
      <button
        type="button"
        onClick={openDocument}
        disabled={!blobUrl || loading || error}
        className={
          className ??
          "flex w-16 h-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 disabled:cursor-not-allowed"
        }
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        ) : error || !blobUrl ? (
          <FileText className="w-6 h-6 text-gray-400" />
        ) : imageFailed ? (
          <FileText className="w-6 h-6 text-gray-400" />
        ) : (
          <img
            src={blobUrl}
            alt={label ?? "Document preview"}
            className="w-full h-full object-cover"
            onError={() => setImageFailed(true)}
          />
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={openDocument}
      disabled={!blobUrl || loading || error}
      className={
        className ??
        "shrink-0 text-xs font-medium text-[#E6007E] hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
      }
    >
      {loading ? "Loading…" : error ? "Unavailable" : (label ?? "Open")}
    </button>
  );
}
